/** @jsx jsx */
import { jsx } from "theme-ui"
import { useState, useRef, useEffect } from "react"
import { useViewportScroll, useTransform, motion } from "framer-motion"

import LandingButton from "../../../molecules/LandingButton"
import MainTitle from "../../../molecules/MainTitle"
import ViewSwitcher from "../../../molecules/ViewSwitcher"
import BackgroundGradient from "../BackgroundGradient"
import Globe from "../Globe"
import Marker from "../Globe/Marker"
import threeProps from "../Globe/threeProps"
import CategorySwitcher from "../../../molecules/CategorySwitcher"

const Hero = ({
  isMobile,
  data,
  view,
  parentRef,
  categories,
  onViewChange,
  onCategoriesChange,
}) => {
  const elRef = useRef()
  const parentHeight = useRef()
  const viewportHeight = useRef()
  const [height] = useState(
    view === "globe" ||
      (typeof window !== "undefined" && window.isLandingAnimationComplete)
      ? 0
      : "calc(100vh - 90px)"
  )
  const { scrollY } = useViewportScroll()

  const btnOpacity = useTransform(scrollY, value => {
    const elHeight = height ? viewportHeight.current - 90 : 0
    const offsetHeight =
      parentHeight.current + elHeight - viewportHeight.current

    if (
      view === "globe" ||
      (height && value >= 500 && value <= offsetHeight) ||
      (!height && (!parentHeight.current || value <= offsetHeight))
    ) {
      return 1
    } else {
      return 0
    }
  })

  const landingBtnOpacity = useTransform(scrollY, value => {
    return height && value >= 300 ? 0 : 1
  })

  const [btnVisibility, setBtnVisibility] = useState(
    btnOpacity ? "visible" : "hidden"
  )
  const [landingBtnVisibility, setLandingBtnVisibility] = useState(
    landingBtnOpacity ? "visible" : "hidden"
  )

  useEffect(
    () =>
      btnOpacity.onChange(opacity => {
        setBtnVisibility(opacity ? "visible" : "hidden")
      }),
    []
  )

  useEffect(
    () =>
      landingBtnOpacity.onChange(opacity => {
        setLandingBtnVisibility(opacity ? "visible" : "hidden")
      }),
    []
  )

  useEffect(() => {
    parentHeight.current = parentRef.current.offsetHeight
    viewportHeight.current = window.innerHeight
  }, [])

  return (
    <div
      ref={elRef}
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: ["flex-start", "center"],
        justifyContent: "flex-end",
        height: height,
        overflow: "hidden",
      }}
    >
      <div
        sx={{
          position: ["static", "absolute"],
          margin: "auto",
          top: "calc(50% - 170px)",
          left: 0,
          width: "100%",
          transform: "translateZ(0)",
        }}
      >
        <MainTitle subtitle={data.subtitle} />
      </div>
      <Globe isMobile={isMobile} view={view === "globe" ? "interactive" : null}>
        {/* Generate the markeres for Projects */}
        {data.projects
          .filter(
            project =>
              project.location &&
              (!project.category ||
                categories.find(category => category === project.category.id))
          )
          .map(project => (
            <Marker
              key={project.slug.current}
              lat={project.location.lat}
              lon={project.location.lng}
              to={`/project/${project.slug.current}`}
              headline={project.headline ? project.headline : ""}
              title={project.excerpt ? project.excerpt : project.title}
              image={project.coverImage.asset.fixed}
              type={project.category.style}
            />
          ))}
        {/* Generate the markeres for LIVE POSTs */}
        {data.posts
          .filter(
            post =>
              post.location &&
              (!post.category ||
                categories.find(category => category === post.category.id))
          )
          .map(post => (
            <Marker
              key={post.id}
              lat={post.location.lat}
              lon={post.location.lng}
              to={post.livePost.url}
              headline={post.headline ? post.headline : "Live"}
              title={`${
                post.excerpt
                  ? post.excerpt.substring(0, 30)
                  : post.livePost.description.substring(0, 30)
              }...`}
              image={post.livePost.thumbnail_url}
              type={post.category.style}
            />
          ))}
      </Globe>
      <motion.div
        sx={{
          transition: "0.3s",
        }}
        style={{
          opacity: landingBtnOpacity,
          visibility: landingBtnVisibility,
        }}
      >
        <LandingButton
          data={data}
          fadeInDelay={threeProps.globe.landingDuration}
        />
      </motion.div>
      {data.projectsCategories.length > 1 && (
        <motion.div
          sx={{
            position: "fixed",
            zIndex: theme => theme.indexes.categorySwitcher,
            top: 0,
            mt: 3,
            transition: "0.3s",
            alignSelf: "center",
          }}
          style={{
            opacity: btnOpacity,
            visibility: btnVisibility,
          }}
        >
          <CategorySwitcher
            onCategoryChange={onCategoriesChange}
            categories={data.projectsCategories}
            active={categories}
          />
        </motion.div>
      )}
      <motion.div
        sx={{
          position: "fixed",
          zIndex: 1,
          bottom: 0,
          alignSelf: "center",
          mb: 5,
          transition: "0.3s",
        }}
        style={{
          opacity: btnOpacity,
          visibility: btnVisibility,
        }}
      >
        <ViewSwitcher view={view} onViewChange={onViewChange} />
      </motion.div>
      <BackgroundGradient />
    </div>
  )
}

export default Hero
